import * as React from 'react';
import { Modal, Form, Button } from 'antd';
import { observer } from 'mobx-react-lite';
import { MLModel } from '../../../ml_models/types';
import MLModelSelector from '../MLModelSelector/MLModelSelector';

interface Props {
    isVisible: boolean;
    setIsVisible: (visible: boolean) => void;
    mlModelList: MLModel[];
    submitCallback: (modelId: string) => void;
    modelId?: string;
}

/**
 * Dialog component for selecting ML models for validation
 * Allows users to select from a list of available ML models
 */
const ValidationModelSelectionDialog: React.FC<Props> = ({
    isVisible,
    setIsVisible,
    mlModelList,
    submitCallback,
    modelId
}) => {
    const [form] = Form.useForm();

    // Reset form when dialog visibility changes
    React.useEffect(() => {
        if (!isVisible) {
            form.resetFields();
        }
    }, [isVisible, form]);

    // Handle form submission
    const handleOk = async () => {
        try {
            const values = await form.validateFields();

            if (values.modelId) {
                submitCallback(values.modelId);
                handleCancel(); // Close dialog after successful submission
            }
        } catch (error) {
            // Form validation failed, user will see validation messages
        }
    };

    // Handle dialog cancellation
    const handleCancel = () => {
        form.resetFields();
        setIsVisible(false);
    };

    return (
        <Modal
            title="Select Validation Model"
            visible={isVisible}
            onCancel={handleCancel}
            maskClosable={false}
            closable={false}
            destroyOnClose
            width={500}
            className="alpha-portal-dialog"
            footer={[
                <Button
                    key="cancel"
                    data-id="validation-model-dialog-cancel"
                    className="light"
                    size="large"
                    onClick={handleCancel}
                >
                    Cancel
                </Button>,
                <Button
                    key="submit"
                    data-id="validation-model-dialog-submit"
                    type="primary"
                    size="large"
                    onClick={handleOk}
                >
                    Select Model
                </Button>
            ]}
        >
            <Form form={form} layout="vertical" preserve={false}>
                <Form.Item
                    name="modelId"
                    label="Model"
                    rules={[{ required: true, message: 'Please select a model' }]}
                    initialValue={modelId}
                >
                    <MLModelSelector models={mlModelList} isLoading={false} placeholder="Select a validation model" />
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default observer(ValidationModelSelectionDialog);
